import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, CreditCard, LogOut, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logoutRemote } from '@/features/auth/services/auth-service'
import { WorkspaceUpgradeDialog } from '@/features/billing/components/workspace-upgrade-dialog'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  formatMembershipPlan,
  formatMembershipStatus,
} from '@/features/billing/utils/membership-labels'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] ?? '用') + (parts[1]?.[0] ?? '')
}

export function AuthUserMenu({ variant = 'workspace' }: { variant?: 'workspace' | 'marketing' }) {
  const navigate = useNavigate()
  const session = useAuthSessionStore((state) => state.session)
  const clearSession = useAuthSessionStore((state) => state.clearSession)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)
  const [workspaceUpgradeOpen, setWorkspaceUpgradeOpen] = useState(false)

  if (!session) return null

  const membershipLabel =
    formatMembershipPlan(session.membership?.planName, session.membership?.planCode) ||
    formatMembershipStatus(session.membership?.status)

  const handleLogout = async () => {
    await logoutRemote()
    clearSession()
    navigate({ to: '/login' })
  }

  const handleMembershipOpen = () => {
    if (variant === 'workspace') {
      setWorkspaceUpgradeOpen(true)
      return
    }
    openMembershipDialog()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={variant === 'marketing' ? 'size-10 rounded-full border-0 bg-[#eef0ff] p-0 text-[#5255f6] shadow-none hover:bg-[#e2e5ff]' : 'h-8 max-w-[11.5rem] gap-1.5 rounded-full px-2.5'}
          aria-label={`${session.user.name}，${membershipLabel}`}
        >
          {session.user.avatarUrl ? (
            <img
              src={session.user.avatarUrl}
              alt={session.user.name}
              className="h-5 w-5 rounded-full object-cover"
            />
          ) : (
            <span className={variant === 'marketing' ? 'flex size-7 items-center justify-center rounded-full bg-white/70 text-xs font-semibold text-[#5255f6]' : 'flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary'}>
              {getInitials(session.user.name)}
            </span>
          )}
          <span className={variant === 'marketing' ? 'sr-only' : 'hidden min-w-0 max-w-[7.5rem] truncate text-sm font-medium sm:block'}>
            {session.user.name}
          </span>
          <ChevronDown className={variant === 'marketing' ? 'sr-only' : 'hidden h-3.5 w-3.5 text-muted-foreground sm:block'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={variant === 'marketing' ? 'w-56 border-[#e5e7eb] bg-white text-[#101727] shadow-[0_16px_36px_rgba(16,23,39,.12)] [--popover:#fff] [--popover-foreground:#101727] [--muted:#f5f6f8] [--muted-foreground:#697184] [--accent:#f5f6f8] [--accent-foreground:#101727]' : 'w-56'}>
        <DropdownMenuLabel>
          <div className="space-y-1">
            <div className="font-medium">{session.user.name}</div>
            <div className="text-xs font-normal text-muted-foreground">{session.user.email}</div>
            <div className="pt-1 text-xs font-normal text-muted-foreground">当前会员：{membershipLabel}</div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/settings">
            <User className="h-4 w-4" />
            设置
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleMembershipOpen}>
          <CreditCard className="h-4 w-4" />
          开通会员
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout}>
          <LogOut className="h-4 w-4" />
          退出登录
        </DropdownMenuItem>
      </DropdownMenuContent>
      {variant === 'workspace' ? <WorkspaceUpgradeDialog open={workspaceUpgradeOpen} onOpenChange={setWorkspaceUpgradeOpen} membership={session.membership} /> : null}
    </DropdownMenu>
  )
}
